import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { AISLES } from '../db/database';
import {
  Check, Trash2, Plus, ShoppingCart, X,
  ChevronDown, ChevronRight, Package,
} from 'lucide-react';
import { Button, EmptyState, Modal } from '../components/common';

export default function GroceryPage() {
  const {
    groceryItems, toggleGroceryItem, removeGroceryItem,
    clearCheckedItems, clearGroceryList, addToGroceryList,
  } = useApp();

  const [newItem, setNewItem] = useState('');
  const [collapsedAisles, setCollapsedAisles] = useState(new Set());
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const groupedItems = useMemo(() => {
    const groups = {};
    groceryItems.forEach((item) => {
      const aisle = item.aisle || 'Other';
      if (!groups[aisle]) groups[aisle] = [];
      groups[aisle].push(item);
    });
    const sorted = {};
    AISLES.forEach((aisle) => {
      if (groups[aisle]) {
        sorted[aisle] = groups[aisle].sort((a, b) => {
          if (a.checked !== b.checked) return a.checked ? 1 : -1;
          return a.item.localeCompare(b.item);
        });
      }
    });
    Object.keys(groups).forEach((aisle) => { if (!sorted[aisle]) sorted[aisle] = groups[aisle]; });
    return sorted;
  }, [groceryItems]);

  const toggleAisle = (aisle) => {
    setCollapsedAisles((prev) => {
      const next = new Set(prev);
      if (next.has(aisle)) next.delete(aisle); else next.add(aisle);
      return next;
    });
  };

  const handleAddItem = async () => {
    if (!newItem.trim()) return;
    await addToGroceryList([{ item: newItem.trim(), qty: '', unit: '' }]);
    setNewItem('');
  };

  const totalItems = groceryItems.length;
  const checkedItems = groceryItems.filter((i) => i.checked).length;
  const progress = totalItems > 0 ? (checkedItems / totalItems) * 100 : 0;

  const AddForm = (
    <div className="flex gap-2">
      <input
        type="text"
        placeholder="Add an item…"
        value={newItem}
        onChange={(e) => setNewItem(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
        className="flex-1 px-3 py-2 border border-gray-300 dark:border-night-card rounded-lg bg-white dark:bg-night-card text-sm text-gray-900 dark:text-night-text placeholder-gray-400 dark:placeholder-night-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
      <Button onClick={handleAddItem} disabled={!newItem.trim()} aria-label="Add item" className="min-h-0 py-2 px-3">
        <Plus size={18} />
      </Button>
    </div>
  );

  if (totalItems === 0) {
    return (
      <div className="space-y-4">
        <EmptyState
          icon={<ShoppingCart size={48} className="text-gray-400 dark:text-night-muted" aria-hidden="true" />}
          title="Your grocery list is empty"
          description="Add items manually or generate a list from your meal plan."
        />
        <div className="px-2">{AddForm}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card rounded-xl p-3 space-y-2">
        <div className="h-2 rounded-full bg-gray-200 dark:bg-night-card overflow-hidden">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${progress}%` }}
            role="progressbar"
            aria-valuenow={Math.round(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <div className="text-xs text-gray-600 dark:text-night-muted text-center">
          {checkedItems} of {totalItems} items
        </div>
      </div>

      {AddForm}

      <div className="space-y-2">
        {Object.entries(groupedItems).map(([aisle, items]) => {
          const aisleChecked = items.filter((i) => i.checked).length;
          const isCollapsed = collapsedAisles.has(aisle);
          const allChecked = aisleChecked === items.length;

          return (
            <div
              key={aisle}
              className={[
                'bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card rounded-xl overflow-hidden',
                allChecked ? 'opacity-60' : '',
              ].join(' ')}
            >
              <button
                onClick={() => toggleAisle(aisle)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-night-card"
              >
                {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                <Package size={16} className="text-gray-500 dark:text-night-muted" aria-hidden="true" />
                <span className="text-sm font-medium text-gray-900 dark:text-night-text flex-1">{aisle}</span>
                <span className="text-xs text-gray-500 dark:text-night-muted">{aisleChecked}/{items.length}</span>
              </button>

              {!isCollapsed && (
                <div className="divide-y divide-gray-100 dark:divide-night-card">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className={['flex items-center gap-2 px-3 py-2', item.checked ? 'opacity-60' : ''].join(' ')}
                    >
                      <button
                        onClick={() => toggleGroceryItem(item.id)}
                        aria-label={item.checked ? 'Uncheck item' : 'Check item'}
                        aria-checked={!!item.checked}
                        role="checkbox"
                        className={[
                          'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0',
                          item.checked
                            ? 'bg-emerald-500 border-emerald-500 text-white'
                            : 'border-gray-300 dark:border-night-card',
                        ].join(' ')}
                      >
                        {item.checked && <Check size={14} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className={['text-sm text-gray-900 dark:text-night-text', item.checked ? 'line-through' : ''].join(' ')}>
                          {item.qty && `${item.qty} `}
                          {item.unit && `${item.unit} `}
                          {item.item}
                        </div>
                        {item.recipeTitle && (
                          <div className="text-xs text-gray-500 dark:text-night-muted truncate">{item.recipeTitle}</div>
                        )}
                      </div>
                      <button
                        onClick={() => removeGroceryItem(item.id)}
                        aria-label="Remove item"
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-night-card text-gray-400 dark:text-night-muted"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {checkedItems > 0 && (
        <button
          onClick={clearCheckedItems}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-night-muted hover:underline"
        >
          <Trash2 size={16} aria-hidden="true" />
          Clear checked ({checkedItems})
        </button>
      )}

      <button
        onClick={() => setShowClearConfirm(true)}
        className="block text-sm text-red-600 dark:text-red-400 hover:underline"
      >
        Clear entire list
      </button>

      <Modal
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        title="Clear grocery list?"
      >
        <p className="text-sm text-gray-700 dark:text-night-text mb-4">
          Remove all {totalItems} items from your grocery list?
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setShowClearConfirm(false)}>Cancel</Button>
          <Button variant="danger" onClick={() => { clearGroceryList(); setShowClearConfirm(false); }}>
            Clear All
          </Button>
        </div>
      </Modal>
    </div>
  );
}
